import { z } from 'zod';
import {
  I18nStringSchema,
  MoneySchema,
  ShortIdSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';
import { ChallengeRewardSchema } from './challenge.js';
import { SpinWheelConfigSchema } from './spin-wheel.js';

export const LoyaltyConfigSchema = z.object({
  enabled: z.boolean().default(true),
  pointsPerRM: z.number().nonnegative().default(1),
  vehicleMultipliers: z.record(z.string(), z.number().positive()).default({}),
  timeBonuses: z
    .array(
      z.object({
        label: I18nStringSchema,
        dows: z.array(z.number().int().min(0).max(6)),
        timeWindow: z
          .object({
            from: z.string().regex(/^\d{2}:\d{2}$/),
            to: z.string().regex(/^\d{2}:\d{2}$/),
          })
          .optional(),
        multiplier: z.number().positive(),
      }),
    )
    .default([]),
  expiryDays: z.number().int().nonnegative().default(0).describe('0 = never expire'),
  enabledFeatures: z.object({
    tiers: z.boolean().default(true),
    punchCard: z.boolean().default(false),
    achievements: z.boolean().default(true),
    challenges: z.boolean().default(false),
    spinWheel: z.boolean().default(false),
    referrals: z.boolean().default(false),
    birthdayBonus: z.boolean().default(true),
    streaks: z.boolean().default(false),
  }),
  punchCard: z
    .object({
      washesRequired: z
        .number()
        .int()
        .min(2)
        .max(50)
        .default(10)
        .describe('Qualifying paid washes needed to earn one free-wash reward'),
      rewardVoucherTemplateId: z
        .string()
        .optional()
        .describe(
          'Voucher template issued when a card completes (typically a free_service/free_package voucher)',
        ),
    })
    .default({ washesRequired: 10 })
    .describe('Punch-card mechanic: every Nth paid wash earns a free-wash reward voucher'),
  /**
   * Customer referrals ("refer a friend"). Operator configures what each side
   * earns when a referred NEW customer completes their first paid wash. Both
   * sides reuse the challenge/achievement reward union (`points`/`voucher`/
   * `none`) so the award pipeline reuses the same idempotent grant paths.
   * Ships OFF (`enabledFeatures.referrals` false, both rewards `none`) — inert
   * until the operator switches it on and picks the rewards.
   */
  referrals: z
    .object({
      referrerReward: ChallengeRewardSchema.describe(
        'What the referrer earns per converted friend',
      ),
      refereeReward: ChallengeRewardSchema.describe(
        'What the new friend earns on their first paid wash',
      ),
    })
    .default({ referrerReward: { kind: 'none' }, refereeReward: { kind: 'none' } })
    .describe('Dual-reward config for the customer-to-customer referral mechanic'),
  /**
   * Spin Wheel — an operator-configured prize game (opt-in via
   * `enabledFeatures.spinWheel`). The wheel's prize table (segments + odds) and
   * banked-spin cap live inline here, parallel to `punchCard` + `referrals`.
   * Ships EMPTY + disabled — inert until the operator authors >= 2 segments and
   * flips the flag. All prize selection is server-side (secure weighted RNG);
   * see `packages/shared/src/loyalty/spin-wheel.ts` + the `spinTheWheel` callable.
   */
  spinWheel: SpinWheelConfigSchema,
  /**
   * Birthday bonus — a once-a-year treat granted on a PAID wash during the
   * customer's opted-in birth MONTH (month-only, privacy-minimal: no day/year is
   * ever captured — see `UserProfile.birthMonth`). Visit-triggered (there is no
   * proactive/cron gifting — cost-free), gated on `enabledFeatures.birthdayBonus`.
   * Reuses the shared reward union (`points`/`voucher`/`none`) so the award
   * pipeline reuses the same idempotent grant paths. Ships OFF (`reward.none`) —
   * inert until the operator picks a reward, even though the feature flag defaults
   * on. Once-per-year is enforced by `LoyaltyAccount.birthdayBonusYear`.
   */
  birthdayBonus: z
    .object({
      reward: ChallengeRewardSchema.describe(
        'What the customer earns on a paid wash during their birth month (points / voucher / none)',
      ),
    })
    .default({ reward: { kind: 'none' } })
    .describe('Birthday-bonus mechanic config: the once-per-year birth-month reward'),
  promotionsPaused: z.boolean().default(false),
  minMarginPct: z
    .number()
    .min(0)
    .max(100)
    .default(0)
    .describe(
      'Anti-undercut floor (G7): the lowest share of LIST price a promotion may leave standing, ' +
        '0-100. This is NOT a cost margin — the catalogue has no cost field, and inventing one the ' +
        'operator never entered would produce an authoritative-looking number that means nothing. ' +
        'Ships at 0 = OFF, per the rule that every tunable starts at zero until the owner sets it: ' +
        'a guard that fires on day one with a number nobody chose gets dismissed on reflex.',
    ),
});
export type LoyaltyConfig = z.infer<typeof LoyaltyConfigSchema>;

export const TierPerkSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('discount_pct'),
    value: z.number().min(0).max(100),
    description: I18nStringSchema,
  }),
  z.object({
    kind: z.literal('free_upgrade_per_period'),
    period: z.enum(['monthly', 'quarterly']),
    description: I18nStringSchema,
  }),
  z.object({ kind: z.literal('queue_priority'), description: I18nStringSchema }),
  z.object({
    kind: z.literal('bonus_points_multiplier'),
    value: z.number().positive(),
    description: I18nStringSchema,
  }),
  z.object({ kind: z.literal('custom'), description: I18nStringSchema }),
]);
export type TierPerk = z.infer<typeof TierPerkSchema>;

export const TierSchema = z.object({
  id: z.string(),
  rank: z.number().int().nonnegative(),
  name: I18nStringSchema,
  thresholdLifetimePoints: z.number().int().nonnegative(),
  perks: z.array(TierPerkSchema).default([]),
  badge: z.object({ icon: z.string(), colour: z.string() }),
});
export type Tier = z.infer<typeof TierSchema>;

export const VoucherEffectSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pct_off'),
    value: z.number().min(0).max(100),
    appliesTo: z.enum(['cart', 'package', 'service']),
  }),
  z.object({ type: z.literal('fixed_off'), value: MoneySchema, appliesTo: z.literal('cart') }),
  z.object({ type: z.literal('free_service'), serviceId: z.string() }),
  z.object({ type: z.literal('free_package'), packageId: z.string() }),
]);
export type VoucherEffect = z.infer<typeof VoucherEffectSchema>;

export const VoucherTemplateSchema = z.object({
  id: ShortIdSchema,
  kind: z.literal('template'),
  name: I18nStringSchema,
  description: I18nStringSchema,
  effect: VoucherEffectSchema,
  validity: z.object({ from: TimestampSchema, to: TimestampSchema }),
  perCustomerLimit: z.number().int().positive().default(1),
  totalLimit: z.number().int().positive().optional(),
  targeting: z
    .object({
      tierMin: z.number().int().nonnegative().optional(),
      customerTagIn: z.array(z.string()).optional(),
      newCustomerOnly: z.boolean().optional(),
    })
    .default({}),
  active: z.boolean().default(true),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type VoucherTemplate = z.infer<typeof VoucherTemplateSchema>;

/**
 * An IssuedVoucher is a per-customer entitlement to redeem a voucher template.
 *
 * Lives at `users/{ownerUid}/vouchersHeld/{voucherId}`. We snapshot the
 * template's name + effect at issue time so post-issue template edits don't
 * change what the customer was promised (premium-logical: no surprises).
 *
 * The `tenantId` field is the discriminator that lets the customer-pwa filter
 * vouchers down to the current tenant's wallet (loyalty is per-tenant —
 * vouchers don't cross operator boundaries).
 */
export const IssuedVoucherSchema = z.object({
  id: ShortIdSchema,
  kind: z.literal('issued'),
  tenantId: z.string().min(1),
  templateId: ShortIdSchema,
  templateName: I18nStringSchema,
  effect: VoucherEffectSchema,
  ownerUid: UidSchema,
  status: z.enum(['active', 'redeemed', 'expired', 'revoked']),
  issuedAt: TimestampSchema,
  expiresAt: TimestampSchema,
  redeemedAt: TimestampSchema.optional(),
  redeemedOnTicketId: ShortIdSchema.optional(),
});
export type IssuedVoucher = z.infer<typeof IssuedVoucherSchema>;

export const AchievementCriteriaSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('first_wash') }),
  z.object({ kind: z.literal('n_visits'), n: z.number().int().positive() }),
  z.object({ kind: z.literal('streak_n_weeks'), n: z.number().int().positive() }),
  z.object({ kind: z.literal('spent_over'), amount: MoneySchema }),
  z.object({
    kind: z.literal('package_count'),
    packageId: z.string(),
    n: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('add_on_count'),
    addOnId: z.string(),
    n: z.number().int().positive(),
  }),
  z.object({ kind: z.literal('custom'), description: z.string() }),
]);

export const AchievementSchema = z.object({
  id: ShortIdSchema,
  name: I18nStringSchema,
  icon: z.string(),
  description: I18nStringSchema,
  criteria: AchievementCriteriaSchema,
  reward: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('points'), amount: z.number().int().positive() }),
    z.object({ kind: z.literal('voucher'), voucherTemplateId: ShortIdSchema }),
    z.object({ kind: z.literal('none') }),
  ]),
  active: z.boolean().default(true),
});
export type Achievement = z.infer<typeof AchievementSchema>;

export const PointsLedgerEntrySchema = z.object({
  id: ShortIdSchema,
  kind: z.enum(['earned', 'spent', 'expired', 'adjustment']),
  points: z.number().int(),
  basis: z.record(z.string(), z.unknown()),
  at: TimestampSchema,
});
export type PointsLedgerEntry = z.infer<typeof PointsLedgerEntrySchema>;

/**
 * Operator-defined customer tag template. Lives at
 * `tenants/{tid}/customerTags/{tagId}`. Operators define a small set of
 * meaningful tags ("VIP", "Fleet account", "Detailing-only", etc.) on the
 * `/loyalty/tags` admin page; the tag id is what gets referenced by:
 *   - `users/{uid}/customerTagsByTenant/{tid}.tags[]` (which tags are applied
 *      to that customer at this tenant)
 *   - `VoucherTemplate.targeting.customerTagIn[]` (issue voucher only if one
 *      of these tags is set on the customer)
 *
 * `archived` (not `active`) so deleting a tag doesn't break references that
 * point to its id on existing customer or voucher docs — the tag just stops
 * appearing in pickers. Hard-deletes ship later if ever.
 */
export const CustomerTagSchema = z.object({
  id: ShortIdSchema,
  label: I18nStringSchema,
  colour: z.string().optional().describe('Optional hex (#RRGGBB) for the chip background'),
  archived: z.boolean().default(false),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type CustomerTag = z.infer<typeof CustomerTagSchema>;

/**
 * Per-customer tag application doc. Lives at
 * `users/{uid}/customerTagsByTenant/{tenantId}`.
 *
 * Schema is intentionally tiny: just the array of tag ids and an updatedAt.
 * The doc lives under `users/{uid}` so firestore.rules per-uid coverage
 * applies — operators write through the `setCustomerTags` callable (admin SDK
 * bypasses uid rules), customers can read their own tags directly.
 */
export const CustomerTagsByTenantSchema = z.object({
  tags: z.array(ShortIdSchema).default([]),
  updatedAt: TimestampSchema,
});
export type CustomerTagsByTenant = z.infer<typeof CustomerTagsByTenantSchema>;
